import { supabase } from '../supabaseClient';
import { MatchState, Player } from '../matchTypes';
import { updatePlayerStatsFromMatch, processLeagueMatchCompletion, removeLeagueMatchStats } from './statTracking';
import { createPlayerProfile, listPlayerProfiles } from './supabasePlayerProfiles';
import { getActiveSeason } from './leagueSeasons';
import { resolveMatchLifecycle, resolveStatusWithGuard, isStatusRegression } from '../utils/matchLifecycle';

export type MatchSummary = {
  id: string;
  userId: string;
  matchType: string;
  teamAName: string;
  teamBName: string;
  teamALogoUrl?: string | null;
  teamBLogoUrl?: string | null;
  status: string;
  format: string;
  matchData: MatchState;
  allowPlayerStatsView: boolean;
  allowTeamScorecardView: boolean;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
  scheduledAt?: string | null;
  venue?: string | null;
};

export type MatchListSummary = {
  id: string;
  userId: string;
  matchType: string;
  teamAName: string;
  teamBName: string;
  teamALogoUrl?: string | null;
  teamBLogoUrl?: string | null;
  status: string;
  format: string;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
  hasActivity?: boolean;
  legalBalls?: number;
  scheduledAt?: string | null;
  venue?: string | null;
};

/**
 * Test function to verify database connectivity and match creation
 * Can be called from browser console: window.testMatchCreation()
 *
 * Tests both minimal insert (what app needs) and full insert
 */
export async function testMatchCreation(): Promise<void> {
  try {
    console.log('='.repeat(70));
    console.log('🧪 MATCH CREATION TEST - Starting...');
    console.log('='.repeat(70));

    const { data: { session } } = await supabase.auth.getSession();

    if (!session?.user) {
      console.error('❌ FAILED: No authenticated user. Please sign in first.');
      return;
    }

    console.log('✓ Authentication verified');
    console.log('  User ID:', session.user.id);
    console.log('  Email:', session.user.email);

    // TEST 1: Minimal insert (absolute minimum required)
    console.log('\n📝 TEST 1: Minimal Insert (Required Fields Only)');
    console.log('-'.repeat(70));

    const minimalData = {
      id: crypto.randomUUID(),
      user_id: session.user.id,
      status: 'draft',
      overs: 15,
    };

    console.log('Payload:', JSON.stringify(minimalData, null, 2));

    const { data: minimalResult, error: minimalError } = await supabase
      .from('matches')
      .insert(minimalData)
      .select()
      .single();

    if (minimalError) {
      console.error('❌ TEST 1 FAILED:', {
        code: minimalError.code,
        message: minimalError.message,
        details: minimalError.details,
        hint: minimalError.hint
      });

      if (minimalError.code === '23502') {
        const columnMatch = minimalError.message?.match(/column "(\w+)"/);
        console.error(`💥 Missing required column: "${columnMatch?.[1] || 'unknown'}"`);
        console.error('   Run the JWKOBZ_MIGRATION.sql to fix this!');
      }

      return;
    }

    console.log('✓ TEST 1 PASSED: Minimal insert successful');
    console.log('  Match ID:', minimalResult.id);

    // Clean up test 1
    await supabase.from('matches').delete().eq('id', minimalData.id);
    console.log('✓ Test 1 data cleaned up');

    // TEST 2: Full insert (what app actually sends)
    console.log('\n📝 TEST 2: Full Insert (All App Fields)');
    console.log('-'.repeat(70));

    const fullData = {
      id: crypto.randomUUID(),
      user_id: session.user.id,
      match_type: 'INDOOR',
      team_a_name: 'Test Team A',
      team_b_name: 'Test Team B',
      status: 'draft',
      format: 'INDOOR',
      is_public: false,
      match_data: { test: true },
      has_activity: false,
      legal_balls: 0,
      overs: 15,
    };

    console.log('Columns:', Object.keys(fullData).join(', '));

    const { data: fullResult, error: fullError } = await supabase
      .from('matches')
      .insert(fullData)
      .select()
      .single();

    if (fullError) {
      console.error('❌ TEST 2 FAILED:', {
        code: fullError.code,
        message: fullError.message,
        details: fullError.details,
        hint: fullError.hint
      });
      return;
    }

    console.log('✓ TEST 2 PASSED: Full insert successful');
    console.log('  Match ID:', fullResult.id);

    // Clean up test 2
    await supabase.from('matches').delete().eq('id', fullData.id);
    console.log('✓ Test 2 data cleaned up');

    // SUCCESS
    console.log('\n' + '='.repeat(70));
    console.log('✅ ALL TESTS PASSED - Match creation is working!');
    console.log('='.repeat(70));
    console.log('Your Supabase database is correctly configured.');
    console.log('You can now create matches in the app.');
    console.log('='.repeat(70));

  } catch (err) {
    console.error('\n' + '='.repeat(70));
    console.error('💥 EXCEPTION OCCURRED');
    console.error('='.repeat(70));
    console.error(err);
    console.error('\nThis might indicate a connection or permission issue.');
    console.error('Check your VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in .env');
  }
}

// Expose to window for console testing
if (typeof window !== 'undefined') {
  (window as any).testMatchCreation = testMatchCreation;
}

/**
 * Ensure player profiles exist for all players in a match
 * Creates profiles for any player that doesn't have one
 * Updates the match state with profileIds
 */
async function ensurePlayerProfiles(state: MatchState): Promise<void> {
  try {
    console.log('[ensurePlayerProfiles] Starting player profile creation...');

    // Validate auth session first
    const { data: { user }, error: authError } = await supabase.auth.getUser();

    if (authError || !user) {
      console.error('[ensurePlayerProfiles] No authenticated user:', authError);
      throw new Error('Session expired. Please log in again.');
    }

    console.log('[ensurePlayerProfiles] Authenticated user:', user.id);

    // Get all existing profiles for this user
    const existingProfiles = await listPlayerProfiles();
    console.log('[ensurePlayerProfiles] Existing profiles count:', existingProfiles.length);

    const profileMap = new Map(existingProfiles.map(p => [p.name.toLowerCase(), p]));

    // Helper to find or create profile for a player
    const ensureProfile = async (player: Player) => {
      if (!player.name || player.name.trim() === '') {
        console.warn('[ensurePlayerProfiles] Skipping empty player name');
        return;
      }

      const normalizedName = player.name.toLowerCase();
      let profile = profileMap.get(normalizedName);

      if (!profile) {
        console.log('[ensurePlayerProfiles] Creating new profile for:', player.name);

        try {
          // Create new profile
          profile = await createPlayerProfile({
            name: player.name,
            role: "All-rounder", // Default role
            batting_style: "RHB", // Default batting style
            is_guest: true, // Mark as guest until claimed
          });

          if (profile) {
            profileMap.set(normalizedName, profile);
            console.log('[ensurePlayerProfiles] ✅ Profile created:', profile.id);
          } else {
            console.error('[ensurePlayerProfiles] ❌ Failed to create profile for:', player.name);
          }
        } catch (createError) {
          console.error('[ensurePlayerProfiles] ❌ Exception creating profile:', createError);
          // Continue with other players
        }
      } else {
        console.log('[ensurePlayerProfiles] Profile already exists for:', player.name);
      }

      // Link player to profile
      if (profile) {
        player.profileId = profile.id;
        console.log('[ensurePlayerProfiles] Linked player', player.name, 'to profile', profile.id);
      }
    };

    // Process all players from both teams sequentially to prevent
    // duplicate profile creation when two players share the same name
    const allPlayers = [...state.teamAPlayers, ...state.teamBPlayers];
    console.log('[ensurePlayerProfiles] Processing', allPlayers.length, 'players...');

    for (const player of allPlayers) {
      await ensureProfile(player);
    }

    console.log('[ensurePlayerProfiles] ✅ Completed. Profiles created/linked for all players.');
  } catch (error) {
    console.error('[ensurePlayerProfiles] ❌ FATAL ERROR:', error);
    throw error; // Re-throw to prevent match save if player profile creation fails
  }
}

/**
 * Save a match to Supabase database
 */
export async function saveMatchToDb(matchId: string, state: MatchState): Promise<{ success: boolean; error?: string }> {
  try {
    const { data: { session } } = await supabase.auth.getSession();

    if (!session?.user) {
      console.warn('[saveMatchToDb] No authenticated session found');
      return { success: false, error: 'Please sign in to save matches' };
    }

    const user = session.user;

    // CRITICAL: Ensure all players have profiles created BEFORE saving match
    try {
      await ensurePlayerProfiles(state);
    } catch (profileError) {
      console.error('[saveMatchToDb] Failed to create player profiles:', profileError);
      return {
        success: false,
        error: `Failed to save player profiles: ${profileError instanceof Error ? profileError.message : String(profileError)}`
      };
    }

    const legalBalls = state.innings.reduce((total, inning) => {
      return total + inning.deliveries.filter(d => d.isLegal).length;
    }, 0);

    const hasActivity = legalBalls > 0;
    const hasToss = !!state.toss?.winnerTeam;

    const existingMatch = await supabase
      .from('matches')
      .select('completed_at, status')
      .eq('id', matchId)
      .maybeSingle();

    const existingCompletedAt = existingMatch?.data?.completed_at || null;
    const existingStatus = existingMatch?.data?.status;
    const isExistingMatch = !!existingMatch?.data;

    const lifecycle = resolveStatusWithGuard(
      state.status, hasActivity, hasToss, state.isPublic || false, existingStatus
    );
    const matchStatus = lifecycle.status;
    const isPublic = lifecycle.isPublic;

    if (import.meta.env.DEV && matchStatus !== state.status) {
      console.log(`[saveMatchToDb] Status promoted: "${state.status}" → "${matchStatus}" (hasActivity=${hasActivity})`);
    }

    if (isStatusRegression(existingStatus, matchStatus)) {
      console.warn(`[saveMatchToDb] Stale save rejected: DB status="${existingStatus}" vs proposed="${matchStatus}"`);
      return {
        success: false,
        error: 'This match has been updated by another session. Please reload the match to continue.'
      };
    }

    // IMMUTABILITY CHECK: If match is already completed, block all updates
    if (existingCompletedAt || existingStatus === 'completed' || existingStatus === 'ended') {
      console.log('[saveMatchToDb] Match already completed. Blocking update to preserve immutability.');
      return { success: false, error: 'Cannot modify completed match. Match is immutable once completed.' };
    }

    // Calculate overs from state or use default
    const defaultOvers = 15;
    const oversValue = state.oversLimit || defaultOvers;

    // Build match data object with ALL confirmed database columns
    // CRITICAL: Always include overs as a number (never null/undefined)
    // CRITICAL: Always include match_data (the full game state JSONB) - schema has match_data NOT NULL DEFAULT '{}'
    const matchData: any = {
      id: matchId,
      user_id: user.id,
      match_type: state.format,
      team_a_name: state.teamAName,
      team_b_name: state.teamBName,
      team_a_logo_url: state.teamALogo || null,
      team_b_logo_url: state.teamBLogo || null,
      status: matchStatus,
      format: state.format,
      has_activity: hasActivity,
      legal_balls: legalBalls,
      is_public: isPublic,
      overs: oversValue,
      match_data: {
        ...state,
        status: matchStatus,
        innings: Array.isArray(state.innings) ? state.innings : [],
        currentInnings: typeof state.currentInnings === 'number' ? state.currentInnings : 0,
        players: Array.isArray((state as any).players) ? (state as any).players : [],
        teamAPlayers: Array.isArray(state.teamAPlayers) ? state.teamAPlayers : [],
        teamBPlayers: Array.isArray(state.teamBPlayers) ? state.teamBPlayers : [],
      },
      updated_at: new Date().toISOString(),
    };

    // Only set completed_at when completing a match (not on initial creation)
    if (matchStatus === 'completed' && !existingCompletedAt) {
      matchData.completed_at = new Date().toISOString();
    } else if (isExistingMatch && existingCompletedAt) {
      // Preserve existing completed_at if already set
      matchData.completed_at = existingCompletedAt;
    }
    // Don't include completed_at at all for new matches that aren't completed

    if (import.meta.env.DEV) {
      console.log('[saveMatchToDb] Upserting match:', {
        matchId,
        userId: user.id,
        format: state.format,
        status: matchStatus,
        teamA: state.teamAName,
        teamB: state.teamBName,
        isPublic,
        hasActivity,
        legalBalls,
        overs: oversValue,
        matchDataIncluded: true,
        columns: Object.keys(matchData).sort().join(', ')
      });
    }

    // Try the upsert operation
    const { error } = await supabase
      .from('matches')
      .upsert(matchData, { onConflict: 'id' });

    if (error) {
      console.error('[saveMatchToDb] Database error:', {
        code: error.code,
        message: error.message,
        details: error.details,
        hint: error.hint,
        fullError: JSON.stringify(error, null, 2),
        matchData: {
          id: matchData.id,
          user_id: matchData.user_id,
          format: matchData.format,
          status: matchData.status,
          keys: Object.keys(matchData),
          allColumns: Object.keys(matchData).join(', ')
        },
        errorType: error.code === '23502' ? 'NOT_NULL_VIOLATION' :
                   error.code === '42703' ? 'UNDEFINED_COLUMN' :
                   error.code === '42501' ? 'INSUFFICIENT_PRIVILEGE' :
                   'UNKNOWN'
      });

      // Special handling for PGRST204 - column not found in schema cache
      if (error.code === 'PGRST204') {
        const columnMatch = error.message?.match(/'([^']+)' column/);
        const columnName = columnMatch ? columnMatch[1] : 'unknown';
        console.error(`[saveMatchToDb] ⚠️ COLUMN NOT IN SCHEMA: "${columnName}"`);
        console.error(`[saveMatchToDb] This column does not exist in production database`);
        console.error(`[saveMatchToDb] Full error: ${error.message}`);

        const isDev = typeof window !== 'undefined' &&
          (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

        if (isDev) {
          alert(
            `Schema Error: Column "${columnName}" not found\n\n` +
            `${error.message}\n\n` +
            `Check console for details.`
          );
        }

        return {
          success: false,
          error: `Schema error: ${columnName} column not found in database`
        };
      }

      // Special handling for NOT NULL violations (23502)
      if (error.code === '23502') {
        const columnMatch = error.message?.match(/column "(\w+)"/);
        const columnName = columnMatch ? columnMatch[1] : 'unknown';
        console.error(`[saveMatchToDb] NOT NULL constraint violated on column: "${columnName}"`);
        console.error(`[saveMatchToDb] This column requires a value but was not provided or is null`);
        return {
          success: false,
          error: `Required field missing: ${columnName}. Please contact support.`
        };
      }

      // Check if this is a schema/column issue (code 42703 = undefined column)
      if (error.code === '42703' ||
          error.message?.toLowerCase().includes('does not exist') ||
          error.message?.toLowerCase().includes('column')) {

        console.log('[saveMatchToDb] Schema error detected. Attempting fallback minimal insert...');

        // Fallback: Try minimal insert with core required columns + match_data
        const minimalData = {
          id: matchId,
          user_id: user.id,
          match_type: state.format,
          team_a_name: state.teamAName,
          team_b_name: state.teamBName,
          status: matchStatus,
          format: state.format,
          is_public: isPublic,
          match_data: state,
          updated_at: new Date().toISOString(),
        };

        console.log('[saveMatchToDb] Attempting minimal upsert:', minimalData);

        const { data: minimalResult, error: minimalError } = await supabase
          .from('matches')
          .upsert(minimalData, { onConflict: 'id' })
          .select();

        if (minimalError) {
          console.error('[saveMatchToDb] Minimal insert also failed:', {
            code: minimalError.code,
            message: minimalError.message,
            details: minimalError.details,
            hint: minimalError.hint
          });
          return {
            success: false,
            error: `Database schema issue: ${minimalError.message}. Please contact support.`
          };
        }

        console.log('[saveMatchToDb] Minimal insert succeeded:', minimalResult);

        // Now try to update with additional fields one by one
        // DO NOT try to update match_data - it may not exist in production
        try {

          if (matchData.has_activity !== undefined) {
            await supabase
              .from('matches')
              .update({ has_activity: matchData.has_activity })
              .eq('id', matchId);
          }

          if (matchData.legal_balls !== undefined) {
            await supabase
              .from('matches')
              .update({ legal_balls: matchData.legal_balls })
              .eq('id', matchId);
          }

          if (matchData.team_a_logo_url) {
            await supabase
              .from('matches')
              .update({ team_a_logo_url: matchData.team_a_logo_url })
              .eq('id', matchId);
          }

          if (matchData.team_b_logo_url) {
            await supabase
              .from('matches')
              .update({ team_b_logo_url: matchData.team_b_logo_url })
              .eq('id', matchId);
          }

          console.log('[saveMatchToDb] Successfully saved match using fallback method');
          return { success: true };
        } catch (updateErr) {
          console.error('[saveMatchToDb] Error updating additional fields:', updateErr);
          // Match was created successfully, so return success even if extra fields failed
          return { success: true };
        }
      }

      if (error.message?.toLowerCase().includes('row-level security') ||
          error.code === '42501' ||
          error.code === 'PGRST301') {
        return {
          success: false,
          error: 'Save blocked by permissions. Please sign in again or check your connection.'
        };
      }

      if (error.code === '23P01' ||
          error.message?.toLowerCase().includes('cannot regress match status') ||
          error.message?.toLowerCase().includes('check_violation')) {
        console.warn('[saveMatchToDb] DB trigger rejected stale save (status regression blocked)');
        return {
          success: false,
          error: 'This match has been updated by another session. Please reload the match to continue.'
        };
      }

      return { success: false, error: error.message };
    }

    console.log('[saveMatchToDb] Successfully saved match:', matchId);

    // Update league fixture status and table when match status changes
    try {
      const { data: matchRow } = await supabase
        .from('matches')
        .select('fixture_id, league_id, season_id')
        .eq('id', matchId)
        .maybeSingle();

      if (matchRow?.fixture_id) {
        if (matchStatus === 'completed') {
          // Fetch fixture details for team IDs and season
          const { data: fixture } = await supabase
            .from('league_fixtures')
            .select('team_a_id, team_b_id, season_id')
            .eq('id', matchRow.fixture_id)
            .maybeSingle();

          const teamAId = fixture?.team_a_id || '';
          const teamBId = fixture?.team_b_id || '';

          // Determine season_id: match row > fixture > active season
          let seasonId = matchRow.season_id || fixture?.season_id || '';
          if (!seasonId && matchRow.league_id) {
            const activeSeason = await getActiveSeason(matchRow.league_id);
            seasonId = activeSeason?.id || '';
          }

          // Process full league match completion (stats, standings, NRR)
          if (matchRow.league_id && teamAId && teamBId) {
            await processLeagueMatchCompletion(state, {
              matchId,
              leagueId: matchRow.league_id,
              seasonId,
              fixtureId: matchRow.fixture_id,
              teamAId,
              teamBId,
            });
            console.log('[saveMatchToDb] League match completion processed (stats + standings)');
          } else {
            // Fallback: just update fixture status
            const teamAName = state.teamAName || 'Team A';
            const teamBName = state.teamBName || 'Team B';
            const winner = state.result?.winner;
            const margin = state.result?.margin || 0;
            const marginType = state.result?.marginType;
            let resultText = 'No result';
            if (winner === 'A') resultText = `${teamAName} won by ${margin} ${marginType === 'runs' ? 'runs' : 'wickets'}`;
            else if (winner === 'B') resultText = `${teamBName} won by ${margin} ${marginType === 'runs' ? 'runs' : 'wickets'}`;
            else if (winner === 'tie') resultText = 'Match tied';

            await supabase
              .from('league_fixtures')
              .update({ status: 'COMPLETED', result_text: resultText })
              .eq('id', matchRow.fixture_id);
          }

          // Also update old league_table_entries for backward compat
          if (matchRow.league_id) {
            await recalculateLeagueTable(matchRow.league_id);
          }
        } else if (matchStatus === 'live' && hasActivity) {
          await supabase
            .from('league_fixtures')
            .update({ status: 'IN_PROGRESS' })
            .eq('id', matchRow.fixture_id);
          console.log('[saveMatchToDb] Fixture status updated to: IN_PROGRESS');
        }
      }
    } catch (fixtureError) {
      console.error('[saveMatchToDb] Error updating fixture/league stats:', fixtureError);
    }

    if (matchStatus === 'completed' && hasActivity && legalBalls > 0) {
      try {
        await updatePlayerStatsFromMatch(state);
      } catch (statError) {
        console.error('Error updating player stats:', statError);
      }
    }

    return { success: true };
  } catch (err) {
    if (isAbortLike(err)) {
      console.log('[saveMatchToDb] Save aborted (safe) - data preserved locally');
      return { success: false, error: 'Request aborted' };
    }
    console.error('Exception saving match:', err);
    return { success: false, error: String(err) };
  }
}

/**
 * Load a specific match from Supabase
 */
export async function loadMatchFromDb(matchId: string): Promise<MatchState | null> {
  try {
    const { data, error } = await supabase
      .from('matches')
      .select('match_data, team_a_logo_url, team_b_logo_url, status')
      .eq('id', matchId)
      .maybeSingle();

    if (error) {
      if (isAbortLike(error)) {
        console.log('[loadMatchFromDb] Request aborted (safe)');
        return null;
      }
      console.error('Error loading match:', error);
      return null;
    }

    const rawMatchData = data?.match_data as any;

    if (!rawMatchData) {
      console.warn('[loadMatchFromDb] No match_data found for match:', matchId);
      return null;
    }

    const state: MatchState = {
      ...rawMatchData,
      status: (data?.status as string) || rawMatchData.status,
      innings: Array.isArray(rawMatchData.innings) ? rawMatchData.innings : [],
      currentInnings: typeof rawMatchData.currentInnings === 'number' ? rawMatchData.currentInnings : 0,
      players: Array.isArray(rawMatchData.players) ? rawMatchData.players : [],
      teamAPlayers: Array.isArray(rawMatchData.teamAPlayers) ? rawMatchData.teamAPlayers : [],
      teamBPlayers: Array.isArray(rawMatchData.teamBPlayers) ? rawMatchData.teamBPlayers : [],
    };

    if (!state.innings.length && (rawMatchData.status === 'live' || rawMatchData.status === 'Live')) {
      state.innings = [
        {
          battingTeam: rawMatchData.teamAName || 'Team A',
          bowlingTeam: rawMatchData.teamBName || 'Team B',
          score: 0,
          wickets: 0,
          overs: 0,
          balls: [],
          totalBalls: 0,
          legalBalls: 0,
          oversLimit: rawMatchData.oversLimit || 20,
        } as any,
      ];
    }

    if (data?.team_a_logo_url) {
      state.teamALogo = data.team_a_logo_url;
    }
    if (data?.team_b_logo_url) {
      state.teamBLogo = data.team_b_logo_url;
    }

    for (const inn of state.innings) {
      const legalFromDeliveries = Array.isArray(inn.deliveries) && inn.deliveries.length > 0
        ? inn.deliveries.filter((d: any) => d.isLegal).length
        : null;
      if (legalFromDeliveries !== null) {
        inn.legalBalls = legalFromDeliveries;
        inn.totalBalls = legalFromDeliveries;
      } else {
        if (typeof inn.totalBalls !== 'number') {
          inn.totalBalls = inn.legalBalls || 0;
        }
        inn.legalBalls = inn.totalBalls;
      }
    }

    if (state.oversLimit === undefined || state.oversLimit === null) {
      state.oversLimit = 20;
    }

    for (const inn of state.innings) {
      if (inn.oversLimit === undefined || inn.oversLimit === null) {
        inn.oversLimit = state.oversLimit;
      }
    }

    return state;
  } catch (err) {
    if (isAbortLike(err)) {
      console.log('[loadMatchFromDb] Exception aborted (safe)');
      return null;
    }
    console.error('Exception loading match:', err);
    return null;
  }
}

/**
 * List all matches for the current user (excludes deleted matches)
 */
export async function listMatchesFromDb(): Promise<MatchSummary[]> {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      console.log('[listMatchesFromDb] No user authenticated');
      return [];
    }

    console.log('[listMatchesFromDb] Fetching matches for user:', user.id);

    // Defensive query: only apply filters if columns exist
    let query = supabase
      .from('matches')
      .select('*')
      .eq('user_id', user.id)
      .is('deleted_at', null)
      .order('updated_at', { ascending: false, nullsLast: true });

    const { data, error } = await query;

    if (error) {
      console.error('[listMatchesFromDb] Error listing matches:', error);
      throw new Error(`Failed to load matches: ${error.message}`);
    }

    console.log('[listMatchesFromDb] Raw matches received:', data?.length || 0);

    // Filter out deleted and abandoned matches defensively
    const filtered = (data || []).filter((match: any) => {
      const status = match.status || 'draft';
      const deletedAt = match.deleted_at;

      if (deletedAt) return false;
      if (status === 'abandoned' || status === 'deleted') return false;

      return true;
    });

    console.log('[listMatchesFromDb] Filtered matches:', filtered.length);

    return filtered.map((match: any) => {
      const matchData = match.match_data;

      // Migrate old matches: ensure totalBalls field exists
      if (matchData && matchData.innings) {
        for (const inn of matchData.innings) {
          const legalFromDeliveries = Array.isArray(inn.deliveries) && inn.deliveries.length > 0
            ? inn.deliveries.filter((d: any) => d.isLegal).length
            : null;
          if (legalFromDeliveries !== null) {
            inn.legalBalls = legalFromDeliveries;
            inn.totalBalls = legalFromDeliveries;
          } else {
            if (typeof inn.totalBalls !== 'number') {
              inn.totalBalls = inn.legalBalls || 0;
            }
            inn.legalBalls = inn.totalBalls;
          }
        }
      }

      // Load team logos from database columns into match data
      if (matchData) {
        if (match.team_a_logo_url) {
          matchData.teamALogo = match.team_a_logo_url;
        }
        if (match.team_b_logo_url) {
          matchData.teamBLogo = match.team_b_logo_url;
        }
      }

      return {
        id: match.id,
        userId: match.user_id,
        matchType: match.match_type,
        teamAName: match.team_a_name,
        teamBName: match.team_b_name,
        teamALogoUrl: match.team_a_logo_url || null,
        teamBLogoUrl: match.team_b_logo_url || null,
        status: match.status || 'draft',
        format: match.format,
        matchData: matchData,
        allowPlayerStatsView: match.allow_player_stats_view || false,
        allowTeamScorecardView: match.allow_team_scorecard_view || false,
        createdAt: match.created_at,
        updatedAt: match.updated_at,
        completedAt: match.completed_at || null,
        scheduledAt: match.scheduled_at || null,
        venue: match.venue || null,
      };
    });
  } catch (err) {
    console.error('Exception listing matches:', err);
    return [];
  }
}

/**
 * List matches (optimized) - Fetches only summary fields without heavy match_data payload
 * Use this for My Matches list to improve performance
 */
export async function listMatchSummariesFromDb(limit: number = 20, offset: number = 0): Promise<MatchListSummary[]> {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      console.log('[listMatchSummariesFromDb] No user authenticated');
      return [];
    }

    console.log('[listMatchSummariesFromDb] Fetching match summaries for user:', user.id, { limit, offset });

    const { data, error } = await supabase
      .from('matches')
      .select('id, user_id, match_type, team_a_name, team_b_name, team_a_logo_url, team_b_logo_url, status, format, created_at, updated_at, completed_at, has_activity, legal_balls, scheduled_at, venue')
      .eq('user_id', user.id)
      .is('deleted_at', null)
      .order('updated_at', { ascending: false, nullsLast: true })
      .range(offset, offset + limit - 1);

    if (error) {
      console.error('[listMatchSummariesFromDb] Error listing match summaries:', error);
      throw new Error(`Failed to load matches: ${error.message}`);
    }

    console.log('[listMatchSummariesFromDb] Summaries received:', data?.length || 0);

    // Keep abandoned matches visible so users can delete/restore them.
    // Only exclude hard-deleted rows.
    const filtered = (data || []).filter((match: any) => {
      const status = match.status || 'draft';
      if (status === 'deleted') return false;
      return true;
    });

    return filtered.map((match: any) => ({
      id: match.id,
      userId: match.user_id,
      matchType: match.match_type,
      teamAName: match.team_a_name,
      teamBName: match.team_b_name,
      teamALogoUrl: match.team_a_logo_url || null,
      teamBLogoUrl: match.team_b_logo_url || null,
      status: match.status || 'draft',
      format: match.format,
      createdAt: match.created_at,
      updatedAt: match.updated_at,
      completedAt: match.completed_at || null,
      hasActivity: Boolean(match.has_activity) || (typeof match.legal_balls === 'number' && match.legal_balls > 0),
      legalBalls: typeof match.legal_balls === 'number' ? match.legal_balls : 0,
      scheduledAt: match.scheduled_at || null,
      venue: match.venue || null,
    }));
  } catch (err) {
    console.error('Exception listing match summaries:', err);
    return [];
  }
}

/**
 * Delete a match from Supabase (soft delete).
 *
 * The database RLS policy is the authoritative authorisation check — it
 * permits the match creator (via user_id) and org admins (via is_org_admin).
 * This function only guards the session and surfaces RLS 42501 rejections as a
 * clear permission message instead of a raw database error. It does NOT
 * duplicate the ownership/admin decision in application code, so that club
 * administrators who did not create the match can still delete it when the
 * database policy allows it.
 */
export async function deleteMatchFromDb(matchId: string): Promise<{ success: boolean; error?: string }> {
  try {
    console.log('[deleteMatchFromDb] Attempting to delete match:', matchId);

    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) {
      return { success: false, error: 'Your session has expired. Please sign in again to delete matches.' };
    }

    // Fetch league metadata so league stats can be cleaned up after a
    // successful soft delete. The RLS SELECT policy determines visibility here.
    const { data: matchRow } = await supabase
      .from('matches')
      .select('league_id, season_id, fixture_id')
      .eq('id', matchId)
      .maybeSingle();

    if (!matchRow) {
      return { success: false, error: 'Match not found' };
    }

    const { error } = await supabase
      .from('matches')
      .update({
        status: 'deleted',
        deleted_at: new Date().toISOString(),
      })
      .eq('id', matchId);

    if (error) {
      console.error('[deleteMatchFromDb] Database error:', {
        code: error.code,
        message: error.message,
        details: error.details,
        hint: error.hint,
      });
      if (error.code === 'PGRST301') {
        return { success: false, error: 'Your session has expired. Please sign in again to delete matches.' };
      }
      if (error.code === '42501') {
        return { success: false, error: 'You do not have permission to delete this match' };
      }
      return { success: false, error: error.message };
    }

    // Remove league stats if this was a league match
    if (matchRow?.league_id) {
      let seasonId = matchRow.season_id || '';
      if (!seasonId) {
        const activeSeason = await getActiveSeason(matchRow.league_id);
        seasonId = activeSeason?.id || '';
      }
      if (seasonId) {
        await removeLeagueMatchStats(matchId, matchRow.league_id, seasonId);
        console.log('[deleteMatchFromDb] League stats removed for match:', matchId);
      }
    }

    console.log('[deleteMatchFromDb] Successfully deleted match:', matchId);
    return { success: true };
  } catch (err) {
    console.error('[deleteMatchFromDb] Exception deleting match:', err);
    return { success: false, error: String(err) };
  }
}

/**
 * Restore an abandoned match back to draft or live depending on prior activity.
 * Does not touch completed or deleted matches.
 */
export async function restoreAbandonedMatch(matchId: string): Promise<{ success: boolean; error?: string }> {
  try {
    const { data: row, error: fetchError } = await supabase
      .from('matches')
      .select('status, has_activity, legal_balls')
      .eq('id', matchId)
      .maybeSingle();

    if (fetchError) {
      console.error('[restoreAbandonedMatch] Fetch error:', fetchError);
      return { success: false, error: fetchError.message };
    }

    if (!row) {
      return { success: false, error: 'Match not found' };
    }

    if (row.status !== 'abandoned') {
      return { success: false, error: `Cannot restore match with status "${row.status}"` };
    }

    const hasActivity = Boolean(row.has_activity) || (typeof row.legal_balls === 'number' && row.legal_balls > 0);
    const newStatus = hasActivity ? 'live' : 'draft';
    const updatePayload: any = {
      status: newStatus,
      updated_at: new Date().toISOString(),
    };
    if (newStatus === 'live' || newStatus === 'draft') {
      updatePayload.is_public = true;
    }

    const { error: updateError } = await supabase
      .from('matches')
      .update(updatePayload)
      .eq('id', matchId);

    if (updateError) {
      console.error('[restoreAbandonedMatch] Update error:', updateError);
      return { success: false, error: updateError.message };
    }

    console.log('[restoreAbandonedMatch] Restored', matchId, '->', newStatus);
    return { success: true };
  } catch (err) {
    console.error('[restoreAbandonedMatch] Exception:', err);
    return { success: false, error: String(err) };
  }
}

/**
 * Create a new match in the database
 */
export async function createNewMatchInDb(
  state: MatchState,
  leagueId?: string,
  fixtureId?: string,
  seasonId?: string
): Promise<{ success: boolean; matchId?: string; error?: string }> {
  try {
    console.log('[createNewMatchInDb] 🚀 Starting match creation...');
    console.log('[createNewMatchInDb] Supabase URL:', import.meta.env.VITE_SUPABASE_URL);

    const { data: { session } } = await supabase.auth.getSession();

    if (!session?.user) {
      console.error('[createNewMatchInDb] ❌ No authenticated session found');
      alert('You must be signed in to create matches. Please sign in first.');
      return { success: false, error: 'Please sign in to create matches' };
    }

    console.log('[createNewMatchInDb] ✓ User authenticated:', session.user.email);

    const matchId = crypto.randomUUID();
    const defaultOvers = 15;
    const oversValue = state.oversLimit || defaultOvers;

    console.log('[createNewMatchInDb] Creating new match with ID:', matchId);
    console.log('[createNewMatchInDb] Using overs value:', oversValue);

    // MINIMAL INSERT STRATEGY: Insert only absolutely required fields first
    // This ensures match creation never fails due to optional/complex fields
    const minimalData = {
      id: matchId,
      user_id: session.user.id,
      status: 'draft',
      overs: oversValue, // CRITICAL: Always include overs as number
    };

    console.log('[createNewMatchInDb] Step 1: Minimal insert with required fields only');
    console.log('[createNewMatchInDb] Minimal payload:', JSON.stringify(minimalData, null, 2));

    const { data: insertedMatch, error: insertError } = await supabase
      .from('matches')
      .insert(minimalData)
      .select()
      .single();

    if (insertError) {
      console.error('[createNewMatchInDb] ❌ Minimal insert failed:', {
        code: insertError.code,
        message: insertError.message,
        details: insertError.details,
        hint: insertError.hint,
        payload: minimalData
      });

      // Show user-friendly error
      alert(`Database Error creating match:\n\n${insertError.message}\n\nError Code: ${insertError.code}\n\nCheck browser console for full details.`);

      // Check for specific error types
      if (insertError.code === '23502') {
        const columnMatch = insertError.message?.match(/column "(\w+)"/);
        const columnName = columnMatch ? columnMatch[1] : 'unknown';
        return {
          success: false,
          error: `Database requires field: ${columnName}. Please report this error.`
        };
      }

      return {
        success: false,
        error: `Failed to create match: ${insertError.message}`
      };
    }

    console.log('[createNewMatchInDb] ✓ Step 1 complete: Match created with ID:', matchId);

    // Step 2: Update with full match data (best-effort, won't fail match creation)
    // DO NOT include match_data - it may not exist in production
    console.log('[createNewMatchInDb] Step 2: Updating with full match data');

    // AUTO-SET is_public for scheduled/upcoming matches
    // New matches created from fixtures are typically scheduled/upcoming
    // When they go live, saveMatchToDb will ensure is_public=true
    let isPublic = state.isPublic || false;
    const initialStatus = 'draft'; // From minimalData
    if (state.status === 'scheduled' || state.status === 'upcoming' || state.status === 'live') {
      isPublic = true;
      if (import.meta.env.DEV) {
        console.log(`[createNewMatchInDb] Auto-setting is_public=true for status="${state.status}"`);
      }
    }

    const scheduledAt = (state.matchDate && state.matchTime)
      ? new Date(`${state.matchDate}T${state.matchTime}`).toISOString()
      : null;

    const fullUpdateData: any = {
      match_type: state.format,
      team_a_name: state.teamAName,
      team_b_name: state.teamBName,
      team_a_logo_url: state.teamALogo || null,
      team_b_logo_url: state.teamBLogo || null,
      status: state.status || 'draft',
      format: state.format,
      has_activity: false,
      legal_balls: 0,
      is_public: isPublic,
      league_id: leagueId || null,
      fixture_id: fixtureId || null,
      season_id: seasonId || null,
      match_data: state,
      scheduled_at: scheduledAt,
      venue: state.matchLocation || null,
      updated_at: new Date().toISOString(),
    };

    console.log('[createNewMatchInDb] Update payload keys:', Object.keys(fullUpdateData).join(', '));

    const { error: updateError } = await supabase
      .from('matches')
      .update(fullUpdateData)
      .eq('id', matchId);

    if (updateError) {
      console.error('[createNewMatchInDb] ⚠️ Step 2 update failed (match still created as draft):');
      console.error('  - Error Code:', updateError.code);
      console.error('  - Error Message:', updateError.message);
      console.error('  - Error Details:', updateError.details);
      console.error('  - Error Hint:', updateError.hint);
      console.error('  - Payload Keys:', Object.keys(fullUpdateData));

      // The minimal insert succeeded but the full update (which sets the
      // correct status, scheduled_at, teams, etc.) failed. The match row
      // exists as a bare draft with only id/user_id/overs. Report the
      // failure so the caller can surface an error instead of showing
      // a false scheduling-success message.
      if (updateError.code === 'PGRST204') {
        const columnMatch = updateError.message?.match(/'([^']+)' column/);
        const columnName = columnMatch ? columnMatch[1] : 'unknown';
        console.error(`  - ⚠️ Column "${columnName}" not in schema, skipping`);
      }

      return {
        success: false,
        error: `Match created but scheduling details could not be saved: ${updateError.message}`
      };
    } else {
      console.log('[createNewMatchInDb] ✓ Step 2 complete: Full data updated');
    }

    // Update fixture with match_id and status when match is created
    if (fixtureId) {
      await supabase
        .from('league_fixtures')
        .update({
          match_id: matchId,
          status: 'IN_PROGRESS'
        })
        .eq('id', fixtureId)
        .then(() => console.log('[createNewMatchInDb] ✓ Fixture linked to match:', matchId))
        .catch(err => console.warn('[createNewMatchInDb] ⚠️ Fixture update failed:', err));
    }

    console.log('[createNewMatchInDb] ✓✓✓ Match creation complete:', matchId);
    return { success: true, matchId };

  } catch (err) {
    console.error('[createNewMatchInDb] ❌ Exception:', err);
    return { success: false, error: String(err) };
  }
}

/**
 * Update match visibility settings
 */
export async function updateMatchVisibility(
  matchId: string,
  allowPlayerStatsView: boolean,
  allowTeamScorecardView: boolean,
  isPublic?: boolean
): Promise<{ success: boolean; error?: string }> {
  try {
    const updateData: any = {
      allow_player_stats_view: allowPlayerStatsView,
      allow_team_scorecard_view: allowTeamScorecardView,
    };

    if (isPublic !== undefined) {
      updateData.is_public = isPublic;
    }

    const { error } = await supabase
      .from('matches')
      .update(updateData)
      .eq('id', matchId);

    if (error) {
      console.error('Error updating match visibility:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (err) {
    console.error('Exception updating match visibility:', err);
    return { success: false, error: String(err) };
  }
}

/**
 * Update scheduling details (date, time, venue) on an existing match.
 * Only allowed for matches that are not yet live or completed.
 * Updates both the dedicated DB columns and the match_data JSONB.
 */
export async function updateMatchSchedule(
  matchId: string,
  date: string,
  time: string,
  venue: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data: existing, error: fetchError } = await supabase
      .from('matches')
      .select('status, match_data')
      .eq('id', matchId)
      .maybeSingle();

    if (fetchError) {
      console.error('[updateMatchSchedule] Fetch error:', fetchError);
      return { success: false, error: fetchError.message };
    }

    if (!existing) {
      return { success: false, error: 'Match not found' };
    }

    const status = (existing.status || 'draft').toLowerCase();
    if (status === 'live' || status === 'completed' || status === 'ended') {
      return { success: false, error: 'Cannot edit schedule after match has gone live or completed' };
    }

    const scheduledAt = (date && time) ? new Date(`${date}T${time}`).toISOString() : null;
    const matchData = existing.match_data as any || {};
    matchData.matchDate = date;
    matchData.matchTime = time;
    matchData.matchLocation = venue;
    matchData.isScheduled = true;

    // Promote draft matches to scheduled when a date/time is set
    const statusUpdate = status === 'draft' ? 'scheduled' : status;

    const { error } = await supabase
      .from('matches')
      .update({
        status: statusUpdate,
        scheduled_at: scheduledAt,
        venue: venue || null,
        match_data: matchData,
        updated_at: new Date().toISOString(),
      })
      .eq('id', matchId);

    if (error) {
      console.error('[updateMatchSchedule] Update error:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (err) {
    console.error('[updateMatchSchedule] Exception:', err);
    return { success: false, error: String(err) };
  }
}

/**
 * List completed matches for stats calculation
 * Only includes matches that are completed, not deleted, and have legal balls
 */
export async function listCompletedMatchesForStats(userId: string): Promise<MatchSummary[]> {
  try {
    const { data, error } = await supabase
      .from('matches')
      .select('*')
      .eq('user_id', userId)
      .in('status', ['completed', 'ended'])
      .is('deleted_at', null)
      .gt('legal_balls', 0)
      .order('completed_at', { ascending: false, nullsLast: true });

    if (error) {
      console.error('Error listing completed matches for stats:', error);
      return [];
    }

    return (data || []).map((match: any) => {
      const matchData = match.match_data;

      if (matchData && matchData.innings) {
        for (const inn of matchData.innings) {
          const legalFromDeliveries = Array.isArray(inn.deliveries) && inn.deliveries.length > 0
            ? inn.deliveries.filter((d: any) => d.isLegal).length
            : null;
          if (legalFromDeliveries !== null) {
            inn.legalBalls = legalFromDeliveries;
            inn.totalBalls = legalFromDeliveries;
          } else {
            if (typeof inn.totalBalls !== 'number') {
              inn.totalBalls = inn.legalBalls || 0;
            }
            inn.legalBalls = inn.totalBalls;
          }
        }
      }

      // Load team logos from database columns into match data
      if (matchData) {
        if (match.team_a_logo_url) {
          matchData.teamALogo = match.team_a_logo_url;
        }
        if (match.team_b_logo_url) {
          matchData.teamBLogo = match.team_b_logo_url;
        }
      }

      return {
        id: match.id,
        userId: match.user_id,
        matchType: match.match_type,
        teamAName: match.team_a_name,
        teamBName: match.team_b_name,
        teamALogoUrl: match.team_a_logo_url || null,
        teamBLogoUrl: match.team_b_logo_url || null,
        status: match.status,
        format: match.format,
        matchData: matchData,
        allowPlayerStatsView: match.allow_player_stats_view || false,
        allowTeamScorecardView: match.allow_team_scorecard_view || false,
        createdAt: match.created_at,
        updatedAt: match.updated_at,
        completedAt: match.completed_at || null,
      };
    });
  } catch (err) {
    console.error('Exception listing completed matches for stats:', err);
    return [];
  }
}

/**
 * List matches where the current user is a linked player with visibility enabled
 */
export async function listMyParticipatingMatches(): Promise<MatchSummary[]> {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      return [];
    }

    const { data: profiles } = await supabase
      .from('player_profiles')
      .select('id, name')
      .eq('linked_user_id', user.id);

    if (!profiles || profiles.length === 0) {
      return [];
    }

    const profileIds = profiles.map((p) => p.id);

    const { data: allMatches, error } = await supabase
      .from('matches')
      .select('*')
      .eq('allow_player_stats_view', true)
      .is('deleted_at', null)
      .order('updated_at', { ascending: false });

    if (error) {
      console.error('Error listing participating matches:', error);
      return [];
    }

    const matchesWithParticipation: MatchSummary[] = [];

    for (const match of allMatches || []) {
      const matchData = match.match_data as MatchState;

      let isParticipating = false;
      if (matchData && matchData.teamA && matchData.teamB) {
        const allPlayers = [
          ...(matchData.teamA.players || []),
          ...(matchData.teamB.players || []),
        ];

        for (const player of allPlayers) {
          if (player.profileId && profileIds.includes(player.profileId)) {
            isParticipating = true;
            break;
          }
        }
      }

      if (isParticipating) {
        if (matchData && matchData.innings) {
          for (const inn of matchData.innings) {
            const legalFromDeliveries = Array.isArray(inn.deliveries) && inn.deliveries.length > 0
              ? inn.deliveries.filter((d: any) => d.isLegal).length
              : null;
            if (legalFromDeliveries !== null) {
              inn.legalBalls = legalFromDeliveries;
              inn.totalBalls = legalFromDeliveries;
            } else {
              if (typeof inn.totalBalls !== 'number') {
                inn.totalBalls = inn.legalBalls || 0;
              }
              inn.legalBalls = inn.totalBalls;
            }
          }
        }

        // Load team logos from database columns into match data
        if (matchData) {
          if (match.team_a_logo_url) {
            matchData.teamALogo = match.team_a_logo_url;
          }
          if (match.team_b_logo_url) {
            matchData.teamBLogo = match.team_b_logo_url;
          }
        }

        matchesWithParticipation.push({
          id: match.id,
          userId: match.user_id,
          matchType: match.match_type,
          teamAName: match.team_a_name,
          teamBName: match.team_b_name,
          teamALogoUrl: match.team_a_logo_url || null,
          teamBLogoUrl: match.team_b_logo_url || null,
          status: match.status,
          format: match.format,
          matchData: matchData,
          allowPlayerStatsView: match.allow_player_stats_view || false,
          allowTeamScorecardView: match.allow_team_scorecard_view || false,
          createdAt: match.created_at,
          updatedAt: match.updated_at,
          completedAt: match.completed_at || null,
        });
      }
    }

    return matchesWithParticipation;
  } catch (err) {
    console.error('Exception listing participating matches:', err);
    return [];
  }
}

/**
 * Recalculate league table from scratch based on completed fixtures
 * This prevents double-counting and ensures accuracy
 */
export async function recalculateLeagueTable(leagueId: string): Promise<void> {
  try {
    console.log('[recalculateLeagueTable] Starting calculation for league:', leagueId);

    // Get league settings for points
    const { data: league } = await supabase
      .from('leagues')
      .select('points_win, points_tie, points_no_result')
      .eq('id', leagueId)
      .maybeSingle();

    if (!league) {
      console.error('[recalculateLeagueTable] League not found');
      return;
    }

    const pointsWin = league.points_win || 2;
    const pointsTie = league.points_tie || 1;
    const pointsNoResult = league.points_no_result || 1;

    // Get all completed fixtures with match data
    const { data: fixtures } = await supabase
      .from('league_fixtures')
      .select('id, team_a_id, team_a_name, team_b_id, team_b_name, match_id, result_text')
      .eq('league_id', leagueId)
      .eq('status', 'completed')
      .not('match_id', 'is', null);

    if (!fixtures || fixtures.length === 0) {
      console.log('[recalculateLeagueTable] No completed fixtures found');
      return;
    }

    // Get all teams in the league
    const { data: teams } = await supabase
      .from('league_teams')
      .select('id, team_name')
      .eq('league_id', leagueId);

    if (!teams || teams.length === 0) {
      console.log('[recalculateLeagueTable] No teams found');
      return;
    }

    // Initialize table entries for all teams
    const tableEntries: Record<string, {
      teamId: string;
      teamName: string;
      played: number;
      won: number;
      lost: number;
      tied: number;
      noResult: number;
      points: number;
    }> = {};

    teams.forEach(team => {
      tableEntries[team.id] = {
        teamId: team.id,
        teamName: team.team_name,
        played: 0,
        won: 0,
        lost: 0,
        tied: 0,
        noResult: 0,
        points: 0
      };
    });

    // Process each completed fixture
    for (const fixture of fixtures) {
      const teamAId = fixture.team_a_id;
      const teamBId = fixture.team_b_id;
      const resultText = fixture.result_text?.toLowerCase() || '';

      // Skip if teams not in table
      if (!teamAId || !teamBId || !tableEntries[teamAId] || !tableEntries[teamBId]) {
        console.warn('[recalculateLeagueTable] Skipping fixture - team not found:', fixture.id);
        continue;
      }

      // Increment played count
      tableEntries[teamAId].played++;
      tableEntries[teamBId].played++;

      // Determine result and award points
      if (resultText.includes('tied')) {
        // Tie
        tableEntries[teamAId].tied++;
        tableEntries[teamBId].tied++;
        tableEntries[teamAId].points += pointsTie;
        tableEntries[teamBId].points += pointsTie;
      } else if (resultText.includes('no result')) {
        // No result
        tableEntries[teamAId].noResult++;
        tableEntries[teamBId].noResult++;
        tableEntries[teamAId].points += pointsNoResult;
        tableEntries[teamBId].points += pointsNoResult;
      } else if (resultText.includes(fixture.team_a_name.toLowerCase())) {
        // Team A won
        tableEntries[teamAId].won++;
        tableEntries[teamBId].lost++;
        tableEntries[teamAId].points += pointsWin;
      } else if (resultText.includes(fixture.team_b_name.toLowerCase())) {
        // Team B won
        tableEntries[teamBId].won++;
        tableEntries[teamAId].lost++;
        tableEntries[teamBId].points += pointsWin;
      } else {
        console.warn('[recalculateLeagueTable] Could not parse result:', resultText);
      }
    }

    // Delete existing table entries for this league
    await supabase
      .from('league_table_entries')
      .delete()
      .eq('league_id', leagueId);

    // Insert new table entries
    const newEntries = Object.values(tableEntries).map(entry => ({
      league_id: leagueId,
      team_id: entry.teamId,
      team_name: entry.teamName,
      played: entry.played,
      won: entry.won,
      lost: entry.lost,
      tied: entry.tied,
      no_result: entry.noResult,
      points: entry.points,
      updated_at: new Date().toISOString()
    }));

    if (newEntries.length > 0) {
      const { error: insertError } = await supabase
        .from('league_table_entries')
        .insert(newEntries);

      if (insertError) {
        console.error('[recalculateLeagueTable] Error inserting entries:', insertError);
      } else {
        console.log('[recalculateLeagueTable] ✓ Table updated with', newEntries.length, 'entries');
      }
    }

  } catch (err) {
    console.error('[recalculateLeagueTable] Exception:', err);
  }
}

/**
 * Subscribe to realtime updates for a single match.
 * Fires the callback whenever the match row changes (every ball, wicket, innings transition, etc.)
 *
 * Returns an unsubscribe function. ALWAYS call it from the component's cleanup
 * to prevent duplicate subscriptions and memory leaks.
 */
export function subscribeToMatch(
  matchId: string,
  onChange: () => void
): () => void {
  if (!matchId) return () => {};

  const channelName = `public-match-${matchId}`;
  const channel = supabase
    .channel(channelName)
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'matches',
        filter: `id=eq.${matchId}`,
      },
      (payload) => {
        console.log('[subscribeToMatch] Update received for', matchId, (payload.new as any)?.status);
        try {
          onChange();
        } catch (err) {
          console.error('[subscribeToMatch] onChange threw:', err);
        }
      }
    )
    .subscribe((status) => {
      console.log('[subscribeToMatch] status for', matchId, ':', status);
    });

  return () => {
    try {
      supabase.removeChannel(channel);
    } catch (err) {
      console.warn('[subscribeToMatch] removeChannel error (safe):', err);
    }
  };
}

/**
 * Subscribe to realtime updates for all public matches (homepage feed).
 * Returns an unsubscribe function.
 */
export function subscribeToPublicMatches(onChange: () => void): () => void {
  const channel = supabase
    .channel('public-matches-feed')
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'matches',
        filter: 'is_public=eq.true',
      },
      (payload) => {
        console.log('[subscribeToPublicMatches] event:', payload.eventType, (payload.new as any)?.id);
        try {
          onChange();
        } catch (err) {
          console.error('[subscribeToPublicMatches] onChange threw:', err);
        }
      }
    )
    .subscribe((status) => {
      console.log('[subscribeToPublicMatches] status:', status);
    });

  return () => {
    try {
      supabase.removeChannel(channel);
    } catch (err) {
      console.warn('[subscribeToPublicMatches] removeChannel error (safe):', err);
    }
  };
}

function isAbortLike(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const e = err as any;
  return (
    e.name === 'AbortError' ||
    (typeof e.message === 'string' && e.message.toLowerCase().includes('abort')) ||
    (typeof e.details === 'string' && e.details.toLowerCase().includes('abort'))
  );
}

function formatScheduledLabel(dateStr: string | null | undefined): string {
  if (!dateStr) return "TBD";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "TBD";
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const isTomorrow = d.toDateString() === tomorrow.toDateString();
  const timeStr = (d.getHours() !== 0 || d.getMinutes() !== 0)
    ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : "";
  if (isToday) return timeStr ? `Today ${timeStr}` : "Today";
  if (isTomorrow) return timeStr ? `Tomorrow ${timeStr}` : "Tomorrow";
  const dateLabel = d.toLocaleDateString([], { weekday: "short", day: "numeric", month: "short" });
  return timeStr ? `${dateLabel} ${timeStr}` : dateLabel;
}

export const getLivePublicMatches = async () => {
  try {
    // Dev logging: Show Supabase project URL
    const supabaseUrl = supabase.supabaseUrl || 'unknown';
    const urlHost = supabaseUrl ? new URL(supabaseUrl).host : 'unknown';
    console.log('[getLivePublicMatches] Supabase URL host:', urlHost);
    console.log('[getLivePublicMatches] Fetching public matches...');

    let data: any[] | null = null;
    let error: any = null;

    // Try the full query with match_data first
    // Include live, upcoming, completed (and 'ended' for legacy matches)
    const result = await supabase
      .from('matches')
      .select('id, status, is_public, match_type, team_a_name, team_b_name, format, match_data, created_at, updated_at, completed_at, scheduled_at, venue')
      .eq('is_public', true)
      .in('status', ['live', 'upcoming', 'scheduled', 'completed', 'ended'])
      .is('deleted_at', null)
      .order('updated_at', { ascending: false });

    data = result.data;
    error = result.error;

    // If there's a schema error (column doesn't exist), retry without match_data
    if (error && (error.code === '42703' || error.message?.toLowerCase().includes('does not exist'))) {
      console.log('[getLivePublicMatches] Schema error detected, retrying without match_data column...');

      const fallbackResult = await supabase
        .from('matches')
        .select('id, status, is_public, match_type, team_a_name, team_b_name, format, created_at, updated_at, completed_at, scheduled_at, venue')
        .eq('is_public', true)
        .in('status', ['live', 'upcoming', 'scheduled', 'completed', 'ended'])
        .is('deleted_at', null)
        .order('updated_at', { ascending: false });

      data = fallbackResult.data;
      error = fallbackResult.error;

      if (!error) {
        console.log('[getLivePublicMatches] Fallback query succeeded');
        // Add empty match_data to each result
        data = (data || []).map(match => ({ ...match, match_data: {} }));
      }
    }

    // Dev logging: Show query results count
    console.log('[getLivePublicMatches] Rows returned:', data?.length ?? 0);

    if (import.meta.env.DEV && data && data.length > 0) {
      console.log('[getLivePublicMatches] First 3 rows sample:',
        data.slice(0, 3).map(m => ({
          id: m.id,
          status: m.status,
          is_public: m.is_public,
          completed_at: m.completed_at,
          deleted_at: m.deleted_at || 'null'
        }))
      );
    }

    if (error) {
      if (isAbortLike(error)) {
        console.log('[getLivePublicMatches] Request aborted (safe)');
        return [];
      }
      console.error('[getLivePublicMatches] Database error:', {
        message: error.message,
        code: error.code,
        details: error.details,
        hint: error.hint,
        supabaseUrl: urlHost
      });
      return [];
    }

    if (!data || data.length === 0) {
      console.log('[getLivePublicMatches] No public matches found. Query returned 0 rows.');
      console.log('[getLivePublicMatches] Filters applied: is_public=true, deleted_at=null, status IN (live,upcoming,draft,completed,ended)');
      return [];
    }

    console.log('[getLivePublicMatches] Found', data.length, 'public matches:',
      data.map(m => ({ id: m.id, status: m.status, is_public: m.is_public, teamA: m.team_a_name, teamB: m.team_b_name })));

    // Transform to match the expected format for PublicHomePage
    const matches = data.map(match => {
      const matchData = match.match_data as any;
      const isLive = match.status === 'live';
      const isUpcoming = match.status === 'scheduled' || match.status === 'upcoming';
      const isCompleted = match.status === 'completed' || match.status === 'ended';

      // Calculate scores from innings data - FILTER OUT GHOST INNINGS
      let teamAScore = 0;
      let teamBScore = 0;
      let teamAWickets = 0;
      let teamBWickets = 0;
      let overs = '0.0';
      let teamAHasInnings = false;
      let teamBHasInnings = false;

      if (matchData?.innings && matchData.innings.length > 0) {
        const currentIdx = typeof matchData.currentInnings === 'number' ? matchData.currentInnings : 0;
        // Filter out ghost innings (0/0 with no activity) but ALWAYS keep the
        // currently-batting innings so a freshly-started 2nd innings is shown
        // as 0/0 rather than "Yet to bat".
        const activeInnings = matchData.innings.filter((inning: any, idx: number) => {
          if (idx === currentIdx) return true;
          const totalRuns = inning.totalRuns || 0;
          const wickets = inning.wickets || 0;
          const totalBalls = inning.totalBalls || 0;
          const hasDeliveries = Array.isArray(inning.deliveries) && inning.deliveries.length > 0;
          return totalRuns > 0 || wickets > 0 || totalBalls > 0 || hasDeliveries;
        });

        // Get scores for each team from their batting innings (only active innings)
        activeInnings.forEach((inning: any) => {
          const originalIdx = matchData.innings.indexOf(inning);

          if (inning.battingTeam === 'A') {
            teamAHasInnings = true;
            teamAScore = inning.totalRuns || 0;
            teamAWickets = inning.wickets || 0;
            if (originalIdx === matchData.currentInnings) {
              const totalBalls = inning.totalBalls || 0;
              const oversNum = Math.floor(totalBalls / 6);
              const balls = totalBalls % 6;
              overs = `${oversNum}.${balls}`;
            }
          } else if (inning.battingTeam === 'B') {
            teamBHasInnings = true;
            teamBScore = inning.totalRuns || 0;
            teamBWickets = inning.wickets || 0;
            if (originalIdx === matchData.currentInnings) {
              const totalBalls = inning.totalBalls || 0;
              const oversNum = Math.floor(totalBalls / 6);
              const balls = totalBalls % 6;
              overs = `${oversNum}.${balls}`;
            }
          }
        });
      }

      return {
        id: match.id,
        teamA: match.team_a_name,
        teamB: match.team_b_name,
        status: isLive ? 'live' : isUpcoming ? 'upcoming' : 'completed',
        scoreA: isLive || isCompleted ? `${teamAScore}/${teamAWickets}` : undefined,
        scoreB: (isLive || isCompleted) && teamBHasInnings ? `${teamBScore}/${teamBWickets}` : undefined,
        overs: isLive ? overs : undefined,
        format: match.format,
        result: isCompleted ? matchData?.result : undefined,
        startTime: isUpcoming
          ? (match.scheduled_at
            ? formatScheduledLabel(match.scheduled_at)
            : matchData?.matchDate
              ? formatScheduledLabel(
                  matchData.matchTime
                    ? new Date(`${matchData.matchDate}T${matchData.matchTime}`).toISOString()
                    : matchData.matchDate
                )
              : "Starting Soon")
          : undefined,
        scheduledAt: match.scheduled_at || null,
        venue: match.venue || matchData?.matchLocation || matchData?.venue || undefined,
        matchType: match.match_type || undefined,
        lastUpdated: new Date(match.updated_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        matchData: matchData // Include full match data for scorecard display
      };
    });

    console.log('[getLivePublicMatches] Transformed matches:',
      matches.map(m => ({ id: m.id, status: m.status, scoreA: m.scoreA, scoreB: m.scoreB })));

    return matches;
  } catch (err) {
    if (isAbortLike(err)) {
      console.log('[getLivePublicMatches] Fetch aborted (safe)');
      return [];
    }
    console.error('[getLivePublicMatches] Exception:', err);
    return [];
  }
};

/**
 * Get a single public match by ID (for public match viewer)
 */
export const getPublicMatchById = async (matchId: string) => {
  try {
    // Dev logging: Show Supabase URL
    const supabaseUrl = supabase.supabaseUrl || 'unknown';
    const urlHost = supabaseUrl ? new URL(supabaseUrl).host : 'unknown';
    console.log('[getPublicMatchById] Supabase URL host:', urlHost);
    console.log('[getPublicMatchById] Fetching match:', matchId);
    console.log('[getPublicMatchById] Filters: id=' + matchId + ', is_public=true, deleted_at=null');

    const { data, error } = await supabase
      .from('matches')
      .select('id, status, is_public, match_type, team_a_name, team_b_name, format, match_data, updated_at, created_at')
      .eq('id', matchId)
      .eq('is_public', true)
      .is('deleted_at', null)
      .maybeSingle();

    if (error) {
      if (isAbortLike(error)) {
        console.log('[getPublicMatchById] Request aborted (safe)');
        return null;
      }
      console.error('[getPublicMatchById] Database error:', {
        matchId,
        message: error.message,
        code: error.code,
        details: error.details,
        hint: error.hint,
        supabaseUrl: urlHost
      });
      return null;
    }

    if (!data) {
      console.log('[getPublicMatchById] Match not found or not public:', matchId);
      console.log('[getPublicMatchById] Possible reasons: 1) Match ID does not exist, 2) is_public=false, 3) deleted_at is not null');
      return null;
    }

    console.log('[getPublicMatchById] Found match:', {
      id: data.id,
      status: data.status,
      is_public: data.is_public,
      teamA: data.team_a_name,
      teamB: data.team_b_name
    });

    const matchData = data.match_data as any;
    const isLive = data.status === 'live';
    const isUpcoming = data.status === 'scheduled' || data.status === 'upcoming';
    const isCompleted = data.status === 'completed' || data.status === 'ended';

    // Build innings array with scores. Filter out ghost innings (0/0 with no
    // activity) but ALWAYS include the currently-batting innings so a newly
    // started 2nd innings renders with 0/0 instead of being hidden.
    const innings = [];
    if (matchData?.innings && matchData.innings.length > 0) {
      const currentIdx = typeof matchData.currentInnings === 'number' ? matchData.currentInnings : 0;
      matchData.innings.forEach((inning: any, idx: number) => {
        const totalRuns = inning.totalRuns || 0;
        const wickets = inning.wickets || 0;
        const totalBalls = inning.totalBalls || 0;
        const hasDeliveries = Array.isArray(inning.deliveries) && inning.deliveries.length > 0;
        const isCurrent = idx === currentIdx;

        if (isCurrent || totalRuns > 0 || wickets > 0 || totalBalls > 0 || hasDeliveries) {
          innings.push({
            battingTeam: inning.battingTeam === 'A' ? data.team_a_name : data.team_b_name,
            runs: totalRuns,
            wickets: wickets,
            overs: Math.floor(totalBalls / 6),
            balls: totalBalls % 6
          });
        }
      });
    }

    // Build current batsmen array
    const currentBatsmen = [];
    if (isLive && matchData?.innings && matchData.innings[matchData.currentInnings]) {
      const currentInning = matchData.innings[matchData.currentInnings];
      const battingTeamPlayers = matchData.currentInnings === 0 ? matchData.teamAPlayers : matchData.teamBPlayers;

      if (currentInning.currentBatsmanIndex !== undefined && battingTeamPlayers[currentInning.currentBatsmanIndex]) {
        const striker = battingTeamPlayers[currentInning.currentBatsmanIndex];
        currentBatsmen.push({
          name: striker.name,
          runs: striker.runs || 0,
          balls: striker.balls || 0
        });
      }

      if (currentInning.currentNonStrikerIndex !== undefined && battingTeamPlayers[currentInning.currentNonStrikerIndex]) {
        const nonStriker = battingTeamPlayers[currentInning.currentNonStrikerIndex];
        currentBatsmen.push({
          name: nonStriker.name,
          runs: nonStriker.runs || 0,
          balls: nonStriker.balls || 0
        });
      }
    }

    // Build current bowler
    let currentBowler = null;
    if (isLive && matchData?.innings && matchData.innings[matchData.currentInnings]) {
      const currentInning = matchData.innings[matchData.currentInnings];
      const bowlingTeamPlayers = matchData.currentInnings === 0 ? matchData.teamBPlayers : matchData.teamAPlayers;

      if (currentInning.currentBowlerIndex !== undefined && bowlingTeamPlayers[currentInning.currentBowlerIndex]) {
        const bowler = bowlingTeamPlayers[currentInning.currentBowlerIndex];
        const totalBalls = bowler.ballsBowled || 0;
        const overs = Math.floor(totalBalls / 6);
        const balls = totalBalls % 6;
        const oversFormatted = balls > 0 ? `${overs}.${balls}` : `${overs}`;

        currentBowler = {
          name: bowler.name,
          overs: oversFormatted,
          runs: bowler.runsConceded || 0,
          wickets: bowler.wickets || 0
        };
      }
    }

    // Build current over deliveries (all balls in the current over, including illegals)
    const currentOverBalls: any[] = [];
    let ballsPerOver = 6;
    if (isLive && matchData?.innings && matchData.innings[matchData.currentInnings]) {
      const currentInning = matchData.innings[matchData.currentInnings];
      const deliveries = currentInning.deliveries || [];
      if (matchData.ballsPerOver) ballsPerOver = matchData.ballsPerOver;

      // Find where the current over starts by counting legal balls
      let legalCount = 0;
      let currentOverStart = 0;
      for (let i = 0; i < deliveries.length; i++) {
        if (deliveries[i].isLegal) {
          legalCount++;
          if (legalCount % ballsPerOver === 1 && legalCount > 1) {
            // This legal ball starts a new over - but we want the PREVIOUS over start
          }
        }
      }
      // Determine current over number
      const totalLegal = deliveries.filter((d: any) => d.isLegal).length;
      const currentOverNumber = Math.floor(totalLegal / ballsPerOver);
      const ballsInCurrentOver = totalLegal % ballsPerOver;

      // Walk deliveries to find where current over starts
      let legalSeen = 0;
      const overStartLegal = currentOverNumber * ballsPerOver;
      currentOverStart = 0;
      for (let i = 0; i < deliveries.length; i++) {
        if (legalSeen === overStartLegal) {
          currentOverStart = i;
          break;
        }
        if (deliveries[i].isLegal) {
          legalSeen++;
        }
      }
      // If all legal balls counted and overStartLegal matches, start is at end
      if (legalSeen < overStartLegal) {
        currentOverStart = deliveries.length;
      }
      // Edge case: if ballsInCurrentOver === 0 and totalLegal > 0, we just completed an over
      // Show the just-completed over until a new delivery arrives
      if (ballsInCurrentOver === 0 && totalLegal > 0) {
        const prevOverStartLegal = (currentOverNumber - 1) * ballsPerOver;
        legalSeen = 0;
        currentOverStart = 0;
        for (let i = 0; i < deliveries.length; i++) {
          if (legalSeen === prevOverStartLegal) {
            currentOverStart = i;
            break;
          }
          if (deliveries[i].isLegal) {
            legalSeen++;
          }
        }
      }

      // Collect all deliveries from currentOverStart onwards
      for (let i = currentOverStart; i < deliveries.length; i++) {
        const d = deliveries[i];
        currentOverBalls.push({
          runs: d.runs || 0,
          isWicket: d.isWicket || false,
          isWide: !!(d.isWide || d.extraType === 'wide' || d.extraType === 'Wd'),
          isNoBall: !!(d.isNoBall || d.extraType === 'noball' || d.extraType === 'Nb'),
          isLegal: d.isLegal !== false,
          extraRuns: d.extraRuns ?? (d.isWide || d.isNoBall || d.extraType === 'wide' || d.extraType === 'noball' || d.extraType === 'Wd' || d.extraType === 'Nb' ? 1 : 0),
          batRuns: d.batRuns ?? d.runs ?? 0,
        });
      }
    }

    // Determine toss info
    const toss = matchData?.toss || null;
    let tossDisplay: string | null = null;
    if (toss?.winnerTeam && toss?.decision) {
      const tossWinnerName = toss.winnerTeam === 'A' ? data.team_a_name : data.team_b_name;
      tossDisplay = `${tossWinnerName} won the toss and chose to ${toss.decision === 'BAT' ? 'bat' : 'bowl'}`;
    }

    // Determine who bats first
    let battingFirstTeam: string | null = null;
    if (toss?.winnerTeam && toss?.decision) {
      if (toss.decision === 'BAT') {
        battingFirstTeam = toss.winnerTeam === 'A' ? data.team_a_name : data.team_b_name;
      } else {
        battingFirstTeam = toss.winnerTeam === 'A' ? data.team_b_name : data.team_a_name;
      }
    }

    // Determine correct status label
    const hasAnyActivity = innings.length > 0;
    const hasToss = !!(toss?.winnerTeam && toss?.decision);
    let statusLabel: 'pre-match' | 'toss-complete' | 'live' | 'completed';
    if (isCompleted) {
      statusLabel = 'completed';
    } else if (isLive && hasAnyActivity) {
      statusLabel = 'live';
    } else if (hasToss) {
      statusLabel = 'toss-complete';
    } else {
      statusLabel = 'pre-match';
    }

    return {
      id: data.id,
      teamA: data.team_a_name,
      teamB: data.team_b_name,
      teamALogo: matchData?.teamALogo || null,
      teamBLogo: matchData?.teamBLogo || null,
      status: isLive ? 'live' : isUpcoming ? 'upcoming' : 'completed',
      statusLabel,
      format: data.format,
      currentInnings: matchData?.currentInnings || 0,
      innings,
      currentBatsmen: currentBatsmen.length > 0 ? currentBatsmen : null,
      currentBowler,
      lastSixBalls: currentOverBalls.length > 0 ? currentOverBalls : null,
      result: isCompleted ? matchData?.result : undefined,
      toss: toss || null,
      tossDisplay,
      battingFirstTeam,
      venue: matchData?.matchLocation || null,
      matchDate: matchData?.matchDate || null,
      matchTime: matchData?.matchTime || null,
      officials: matchData?.officials || null,
      teamAPlayers: Array.isArray(matchData?.teamAPlayers) ? matchData.teamAPlayers : [],
      teamBPlayers: Array.isArray(matchData?.teamBPlayers) ? matchData.teamBPlayers : [],
      lastUpdated: new Date(data.updated_at).toLocaleString([], {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }),
      createdAt: data.created_at,
      matchData: matchData || null
    };
  } catch (err) {
    if (isAbortLike(err)) {
      console.log('[getPublicMatchById] Fetch aborted (safe)');
      return null;
    }
    console.error('[getPublicMatchById] Exception:', err);
    return null;
  }
};
